import { defineStore, acceptHMRUpdate } from "pinia";
import { computed, ref } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { PaginatedData, PaginationArgs } from "@/types/api/common";
import { useAudienceBuilderStore } from "@/stores/AudienceBuilderStore";
import { useNotificationStore } from "@/stores/NotificationStore";
import { useTaskStore } from "@/stores/TaskStore";
import client from "@/api/openapi-ts";
import { fetchExportDownload } from "@/lib/exports/downloadFile";
import type { components } from "@/api/schema.d.ts";
import isEmpty from "lodash.isempty";
import camelCase from "lodash.camelcase";
import { useExports } from "@/stores/helpers/useExports";
import { findOptionForValue } from "@/helpers/incomeDisplayBands";

type schemas = components["schemas"];

type Loading = {
  aiSummary: boolean;
  surveys: boolean;
  questions: boolean;
  surveyQuestions: boolean;
  surveyDataSingle: boolean;
  surveyDataTrended: boolean;
  surveyDataTrendedQuestionId: undefined | number;
  modeledDataSingle: boolean;
  modeledDataTrended: boolean;
  customDemographics: boolean;
  createCustomDemographic: boolean;
  deleteCustomDemographic: boolean;
  exports: {
    powerpoint: boolean;
    topline: boolean;
    crosstabs: boolean;
    raw_data: boolean;
  };
};

type Errors = {
  surveyDataSingle: string | undefined;
  surveyDataTrended: string | undefined;
  surveyDataTrendedQuestionId:
    { question_id: number; message: string } | undefined;
  modeledDataSingle: string | undefined;
  modeledDataTrended: string | undefined;
  aiSummary: string | undefined;
  exports: {
    powerpoint: string | undefined;
    topline: string | undefined;
    crosstabs: string | undefined;
    raw_data: string | undefined;
  };
};

type TaskIds = {
  surveyDataSingle: string | undefined;
  surveyDataTrended: string | undefined;
  surveyDataTrendedQuestionId: string | undefined;
  modeledDataSingle: string | undefined;
  modeledDataTrended: string | undefined;
  customDemographics: string | undefined;
  aiSummary: string | undefined;
  exports: {
    powerpoint: string | undefined;
    topline: string | undefined;
    crosstabs: string | undefined;
    raw_data: string | undefined;
  };
};

type Demographics = {
  all_adults: boolean;
  age_range: string[];
  custom_age_range: string[];
  children_in_household: string[];
  education_level: string[];
  ethnicity: string[];
  income_level: string[];
  marital_status: string[];
  party: string[];
  sex: string[];
  geography_classification: string[];
};

type Geographies = {
  id: number;
  name: string;
};

type Audiences = {
  id: number;
  name: string;
};

type QuestionPayload = {
  question_id: number;
  kpi_id: number;
  positive_responses: number[];
  negative_responses: number[];
};

const defaultDemographics: Demographics = {
  all_adults: true,
  age_range: [],
  custom_age_range: [],
  children_in_household: [],
  education_level: [],
  ethnicity: [],
  income_level: [],
  marital_status: [],
  party: [],
  sex: [],
  geography_classification: [],
};

const EXPORT_KEYS = ["topline", "crosstabs", "raw_data", "powerpoint"] as const;
type ExportKey = (typeof EXPORT_KEYS)[number];

const EXPORT_LABELS: Record<ExportKey, string> = {
  topline: "Topline",
  crosstabs: "Crosstabs",
  raw_data: "Raw Data",
  powerpoint: "PowerPoint",
};

function toSurveyRequest(
  survey: schemas["ResearchStudioListSurveysResponse"],
): schemas["Survey"] {
  return {
    ...survey,
    fielding_start_date: survey.fielding_start_date ?? undefined,
    fielding_end_date: survey.fielding_end_date ?? undefined,
  };
}

export const useResearchStudioStore = defineStore("researchStudio", () => {
  const { $ws } = usePlugins();
  const audienceBuilderStore = useAudienceBuilderStore();
  const notificationStore = useNotificationStore();
  const taskStore = useTaskStore();
  const cancelTask = async (key: keyof TaskIds) => {
    if (key !== "exports" && taskIds.value[key]) {
      await taskStore.cancelTask(taskIds.value[key] as string);
      taskIds.value[key] = undefined;
    }
  };
  const exportManager = useExports<ExportKey>({
    channel: "ResearchStudio",
    keys: EXPORT_KEYS,
    errorMessage: (key) => `Error creating ${EXPORT_LABELS[key]} export`,
    fetchFile: (file, signal) =>
      fetchExportDownload(
        "/api/v1/research_studio/export/{filename}",
        file,
        signal,
      ),
  });
  const errors = ref<Errors>({
    surveyDataSingle: undefined,
    surveyDataTrended: undefined,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: undefined,
    modeledDataTrended: undefined,
    aiSummary: undefined,
    exports: exportManager.errors,
  });

  const loading = ref<Loading>({
    aiSummary: false,
    surveys: false,
    questions: false,
    surveyQuestions: false,
    surveyDataSingle: false,
    surveyDataTrended: false,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: false,
    modeledDataTrended: false,
    customDemographics: false,
    createCustomDemographic: false,
    deleteCustomDemographic: false,
    exports: exportManager.loading,
  });
  const taskIds = ref<TaskIds>({
    surveyDataSingle: undefined,
    surveyDataTrended: undefined,
    surveyDataTrendedQuestionId: undefined,
    modeledDataSingle: undefined,
    modeledDataTrended: undefined,
    customDemographics: undefined,
    aiSummary: undefined,
    exports: exportManager.taskIds,
  });
  const selectedSurvey = ref<
    schemas["ResearchStudioListSurveysResponse"] | null
  >(null);
  const selectedView = ref<{
    tab: "survey_data" | "modeled_data" | "audience_segmentation";
    chartType: "single" | "trended" | undefined;
  }>({
    tab: "survey_data",
    chartType: "single",
  });
  const surveys = ref<schemas["ResearchStudioListSurveysResponse"][]>([]);
  const questions = ref<
    PaginatedData<schemas["ResearchStudioListSurveyQuestionsResponse"]>
  >({});
  const surveyQuestions =
    ref<schemas["ResearchStudioQuestionsBySurveyIdResponse"][]>();
  const selectedQuestions = ref<
    schemas["ResearchStudioQuestionsBySurveyIdResponse"][]
  >([]);
  const selectedDemographics = ref<Demographics>(defaultDemographics);
  const selectedCustomDemos = ref<
    schemas["ResearchStudioListCustomDemographicsResponse"][]
  >([]);
  const selectedGeographies = ref<Geographies[]>([]);
  const selectedAudiences = ref<Audiences[]>([]);
  const surveyDataSingle = ref<schemas["QuestionData"][]>();
  const surveyDataTrended = ref<schemas["TrendedQuestionData"][]>();
  const modeledDataSingle =
    ref<schemas["ResearchStudioSingleSurveyModeledDataFavorability"][]>();
  const modeledDataTrended = ref<schemas["TrendedQuestionData"][]>();
  const aiSummary = ref<string | undefined>(undefined);
  const customDemographics = ref<
    schemas["ResearchStudioListCustomDemographicsResponse"][]
  >([]);
  const getDemographicName = (
    key: string,
    value: boolean | string | number,
  ): string | undefined => {
    if (key === "all_adults") {
      return value ? "All Adults" : undefined;
    }
    if (key === "children_in_household") {
      return value ? "Children in Household" : "No Children In Household";
    }
    const demographicType = audienceBuilderStore.demographicTypes.find(
      (el: any) => el.name === key,
    );
    if (!demographicType) {
      return undefined;
    }
    return (
      findOptionForValue(demographicType.options, value as number | string)
        ?.name ??
      demographicType.options?.find(
        (el: any) =>
          el.value !== undefined && parseInt(el.value) === Number(value),
      )?.name
    );
  };

  const demographicNames = computed(() => {
    const result: string[] = [];
    for (const [key, value] of Object.entries(selectedDemographics.value)) {
      if (key === "all_adults") {
        if (value) {
          result.push("All Adults");
        }
        continue;
      }
      if (key === "custom_age_range") {
        if (Array.isArray(value) && value.length > 1) {
          result.push(value.join("-"));
        } else if (Array.isArray(value) && value.length === 1) {
          result.push(`${value[0]}+`);
        }
        continue;
      }
      if (Array.isArray(value)) {
        if (!value.length) {
          continue;
        }
        const seenNames = new Set<string>();
        value.forEach((v: boolean | string | number) => {
          const name = getDemographicName(key, v);
          if (!name || seenNames.has(name)) {
            return;
          }
          seenNames.add(name);
          result.push(name);
        });
      }
    }
    selectedCustomDemos.value.forEach((customDemo) => {
      result.push(customDemo.name);
    });
    return result;
  });
  const unreadSurveyIds = computed(() => {
    return notificationStore.notifications
      .filter((n) => !n.read && n.meta_data?.survey_id != null)
      .map((n) => n.meta_data.survey_id);
  });

  const demographicsPayload = (demos: Demographics) => {
    const keyMap = {
      children_in_household: "familieswithchildren",
      race: "ethnicity",
      marital_status: "maritalstatus",
      party: "party_label",
    };

    const payload = Object.keys(demos).reduce(
      (acc, curr) => {
        const key = keyMap[curr as keyof typeof keyMap] || curr;
        if (key === "party_label") {
          const parties = demos[curr as keyof Demographics] as string[];

          acc[key] = parties.map((s: any) => {
            return (
              audienceBuilderStore.demographicTypes
                .find((d: any) => d.name === "party")
                ?.options?.find((o: any) => Number(o.value) === s)?.name ||
              "Unknown"
            );
          });
        } else {
          acc[key] = demos[curr as keyof Demographics];
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    if (payload.age_range.includes("custom-age-range")) {
      payload.age = payload.custom_age_range
        .map((age: any, idx: number) => {
          if (idx === 0 && age === "") {
            return 18;
          }
          return age;
        })
        .filter((v: any) => v !== "");
      payload.age_range = payload.age_range.filter(
        (v: any) => v !== "custom-age-range",
      );
    }
    delete payload.custom_age_range;

    Object.keys(payload).forEach((key) => {
      if (payload[key].length === 0 || key === "all_adults") {
        delete payload[key];
      }
    });

    return payload;
  };

  const surveyResponses = computed(() => {
    return surveyQuestions.value?.map((q) => ({
      kpi: q.kpi,
      kpi_id: q.kpi_id,
      id: q.id,
      positive:
        q.question_choices && q.positive_responses
          ? q.positive_responses.map(
              (p) =>
                (q.question_choices as Record<number, string>)[p as number],
            )
          : undefined,
      positive_label: q.positive_sentiment_label,
      negative:
        q.question_choices && q.negative_responses
          ? q.negative_responses.map(
              (n) =>
                (q.question_choices as Record<number, string>)[n as number],
            )
          : undefined,
      negative_label: q.negative_sentiment_label,
      question_choices: q.question_choices as Record<number, string>,
    }));
  });

  const formatDataForTopline = (surveyData: schemas["QuestionData"][]) => {
    return surveyData
      .map((item) => {
        const allAdultsIndex = item.demographics?.findIndex(
          (demo) => demo.name === "All Adults",
        );

        if (allAdultsIndex === undefined || allAdultsIndex === -1) {
          return null;
        }

        const filteredDemographics = item.demographics
          ? [item.demographics[allAdultsIndex]]
          : [];

        const filteredSurveyResponses =
          item.survey_responses?.map((response) => ({
            ...response,
            data: response.data ? [response.data[allAdultsIndex]] : [],
          })) || [];

        const responseInfo = surveyResponses.value?.find(
          (r) => r.kpi === item.kpi,
        );
        const positiveLabel = responseInfo?.positive_label || "Positive";
        const negativeLabel = responseInfo?.negative_label || "Negative";

        let positiveSum = 0;
        let negativeSum = 0;

        if (responseInfo?.positive && responseInfo?.negative) {
          const positiveResponses = responseInfo.positive;
          const negativeResponses = responseInfo.negative;

          filteredSurveyResponses.forEach((response) => {
            const responseValue = response.data?.[0] || 0;
            const responseName = response.name;

            if (
              responseName &&
              positiveResponses.some(
                (p) =>
                  p === responseName ||
                  p.toLowerCase() === responseName.toLowerCase(),
              )
            ) {
              positiveSum += responseValue;
            } else if (
              responseName &&
              negativeResponses.some(
                (n) =>
                  n === responseName ||
                  n.toLowerCase() === responseName.toLowerCase(),
              )
            ) {
              negativeSum += responseValue;
            }
          });
        }

        const surveyResponsesWithAggregates = [
          ...filteredSurveyResponses.map((response) => ({
            name: response.name,
            data: response.data,
          })),
          {
            name: positiveLabel,
            data: [positiveSum],
          },
          {
            name: negativeLabel,
            data: [negativeSum],
          },
        ];
        return {
          ...item,
          demographics: filteredDemographics,
          survey_responses: surveyResponsesWithAggregates,
          positive_label: positiveLabel,
          negative_label: negativeLabel,
        };
      })
      .filter((item) => item !== null);
  };

  const formatDataForCrosstabs = (dataType: "survey_data" | "modeled_data") => {
    if (dataType === "survey_data") {
      const surveyData = surveyDataSingle.value?.map((d: any) => {
        const responses = surveyResponses.value?.find(
          (r: any) => r.kpi === d.kpi,
        );
        if (responses) {
          return {
            kpi: d.kpi,
            question_text: d.question_text,
            demographics: d.demographics,
            survey_responses: d.survey_responses,
            positive_label: responses.positive_label,
            positive_responses: responses.positive,
            negative_label: responses.negative_label,
            negative_responses: responses.negative,
          };
        }
        return {
          kpi: d.kpi,
          question_text: d.question_text,
          demographics: d.demographics,
          survey_responses: d.survey_responses,
        };
      });
      return surveyData;
    } else if (dataType === "modeled_data") {
      const surveyData = modeledDataSingle.value?.map((q: any) => {
        if (q.favorability) {
          const demographics: { name: string; sample_size: number }[] = [];
          const positiveData: number[] = [];
          const negativeData: number[] = [];
          Object.values(
            q.favorability as Record<
              string,
              Record<string, schemas["Favorability"]>
            >,
          ).forEach((categoryData) => {
            Object.entries(categoryData).forEach(([name, value]) => {
              demographics.push({
                name,
                sample_size: value.sample_size,
              });
              positiveData.push(value.favorable);
              negativeData.push(value.unfavorable);
            });
          });
          return {
            kpi: q.kpi,
            question_text: q.questions_and_responses
              .map((question: any) => question.question_text)
              .join("; "),
            demographics: demographics,
            survey_responses: [
              {
                name: q.positive_sentiment_label,
                data: positiveData,
                sentiment: "positive",
              },
              {
                name: q.negative_sentiment_label,
                data: negativeData,
                sentiment: "negative",
              },
            ],
          };
        }
      });
      return surveyData?.filter((q: any) => q !== undefined);
    }
  };

  const formatDataForAiSummary = () => {
    return formatDataForCrosstabs("survey_data")?.map((item: any) => {
      const allAdultsIndex = item.demographics?.findIndex(
        (demo: any) => demo.name === "All Adults",
      );
      return {
        ...item,
        negative_responses: undefined,
        positive_responses: undefined,
        demographics: selectedDemographics.value.all_adults
          ? item.demographics
          : item.demographics?.filter(
              (demo: any, index: number) => index !== allAdultsIndex,
            ),
        survey_responses: item.survey_responses?.map((response: any) => {
          return {
            ...response,
            sentiment: undefined,
            data: selectedDemographics.value.all_adults
              ? response.data
              : response.data?.filter(
                  (data: any, index: number) => index !== allAdultsIndex,
                ),
          };
        }),
      };
    });
  };

  return {
    loading,
    errors,
    taskIds,
    selectedSurvey,
    selectedView,
    getChartRequestOrder() {
      const chartMethods = [
        "getSurveyDataSingle",
        "getSurveyDataTrended",
        "getModeledDataSingle",
        "getModeledDataTrended",
      ];
      const firstMethod =
        selectedView.value.tab === "audience_segmentation"
          ? "getSurveyDataSingle"
          : camelCase(
              `get_${selectedView.value.tab}_${selectedView.value.chartType}`,
            );
      const remainingMethods = chartMethods.filter(
        (method) => method !== firstMethod,
      );
      return { firstMethod, remainingMethods };
    },
    surveys,
    questions,
    selectedQuestions,
    selectedDemographics,
    selectedCustomDemos,
    selectedGeographies,
    selectedAudiences,
    customDemographics,
    surveyQuestions,
    surveyDataSingle,
    surveyDataTrended,
    modeledDataSingle,
    modeledDataTrended,
    aiSummary,
    demographicNames,
    unreadSurveyIds,
    resetDemographics() {
      selectedDemographics.value = { ...defaultDemographics };
      selectedCustomDemos.value = [];
    },
    cancelTask,
    surveyResponses,

    async listSurveys(search?: string) {
      loading.value.surveys = true;
      try {
        const { data } = await client.GET("/api/v1/research_studio/surveys", {
          params: {
            query: {
              search: search ?? undefined,
            },
          },
        });
        surveys.value = [
          ...(data as schemas["ResearchStudioListSurveysResponse"][]),
        ].sort((a, b) =>
          (b.fielding_start_date ?? "").localeCompare(
            a.fielding_start_date ?? "",
          ),
        );
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.surveys = false;
      }
    },

    async hasSurveys(
      org_id: number | undefined,
      app_client_id: number | undefined,
    ) {
      const { data } = await client.GET("/api/v1/research_studio/has_surveys", {
        params: { query: { org_id, app_client_id } },
      });
      return data;
    },

    async listQuestions(payload: PaginationArgs) {
      loading.value.questions = true;
      questions.value = {};

      try {
        const { data, error, response } = await client.GET(
          "/api/v1/research_studio/survey_questions",
          {
            params: {
              query: payload,
            },
          },
        );
        if (error) {
          throw error;
        }
        questions.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: data,
        };
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.questions = false;
      }
    },

    async getSurveyQuestions(surveyId: number) {
      loading.value.surveyQuestions = true;
      surveyQuestions.value = [];
      try {
        const { data, error } = await client.GET(
          "/api/v1/research_studio/survey_questions/{survey_id}",
          {
            params: {
              path: {
                survey_id: surveyId,
              },
            },
          },
        );
        if (error) {
          throw error;
        }
        surveyQuestions.value = data;
        loading.value.surveyQuestions = false;
      } catch (error) {
        console.error(error);
        loading.value.surveyQuestions = false;
      }
    },
    async getCustomDemographics() {
      loading.value.customDemographics = true;
      const { data, error } = await client.GET(
        "/api/v1/research_studio/custom_demographics",
      );
      if (error) {
        loading.value.customDemographics = false;
        return;
      }
      customDemographics.value = data ?? [];
      loading.value.customDemographics = false;
    },
    async createCustomDemographic(
      payload: schemas["ResearchStudioCreateCustomDemographicRequest"],
    ) {
      loading.value.createCustomDemographic = true;
      payload.demographics = demographicsPayload(payload.demographics as any);
      const { data } = await client.POST(
        "/api/v1/research_studio/custom_demographic",
        {
          body: payload,
        },
      );
      if (data) {
        customDemographics.value.push({
          id: data.id,
          name: data.name,
          demographics: data.demo_json,
        });
      }
      // TODO: handle error
      loading.value.createCustomDemographic = false;
    },
    async updateCustomDemographic(
      payload: schemas["ResearchStudioUpdateCustomDemographicRequest"],
    ) {
      loading.value.createCustomDemographic = true;
      payload.demographics = demographicsPayload(payload.demographics as any);
      const { data } = await client.PUT(
        "/api/v1/research_studio/custom_demographic",
        {
          body: payload,
        },
      );
      if (data) {
        customDemographics.value = customDemographics.value.map((demo) => {
          if (demo.id === data.id) {
            return {
              ...demo,
              name: data.name,
              demographics: data.demo_json,
            };
          }
          return demo;
        });
      }
      loading.value.createCustomDemographic = false;
    },
    async deleteCustomDemographic(customDemographicId: number) {
      loading.value.deleteCustomDemographic = true;
      try {
        const { data } = await client.DELETE(
          "/api/v1/research_studio/custom_demographic/{custom_demographic_id}",
          {
            params: {
              path: {
                custom_demographic_id: customDemographicId,
              },
            },
          },
        );
        if (data) {
          customDemographics.value = customDemographics.value.filter(
            (d) => d.id !== customDemographicId,
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.deleteCustomDemographic = false;
      }
    },
    async getSurveyDataSingle() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("surveyDataSingle");
      errors.value.surveyDataSingle = undefined;
      loading.value.surveyDataSingle = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchStudioListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research_studio/single_survey_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: q.question_id,
                kpi_id: q.kpi_id,
              })),
              demographics: demographicsPayload(selectedDemographics.value),
              custom_demographics: customDemoIds,
            } as schemas["ResearchStudioSingleSurveyQuestionDataRequest"],
          },
        );
        taskIds.value.surveyDataSingle = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ResearchStudio",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataSingle = undefined;
        loading.value.surveyDataSingle = false;
      }
    },
    async getSurveyDataTrended() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("surveyDataTrended");
      errors.value.surveyDataTrended = undefined;
      loading.value.surveyDataTrended = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchStudioListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research_studio/trended_survey_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: q.question_id,
                kpi_id: q.kpi_id,
                positive_responses: q.positive_responses,
                negative_responses: q.negative_responses,
              })),
              demographics: demographicsPayload(selectedDemographics.value),
              custom_demographics: customDemoIds,
            },
          },
        );
        taskIds.value.surveyDataTrended = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ResearchStudio",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataTrended = undefined;
        loading.value.surveyDataTrended = false;
      }
    },
    async getSurveyDataTrendedQuestionId(question: QuestionPayload) {
      const surveyId = selectedSurvey.value?.id;

      if (!surveyId || !question) return;
      errors.value.surveyDataTrendedQuestionId = undefined;
      loading.value.surveyDataTrendedQuestionId = question.question_id;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchStudioListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research_studio/trended_survey_question_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: [question],
              demographics: demographicsPayload(selectedDemographics.value),
              custom_demographics: customDemoIds,
            },
          },
        );
        taskIds.value.surveyDataTrendedQuestionId = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ResearchStudio",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.surveyDataTrendedQuestionId = undefined;
        loading.value.surveyDataTrendedQuestionId = undefined;
      }
    },
    async getModeledDataSingle() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("modeledDataSingle");
      errors.value.modeledDataSingle = undefined;
      loading.value.modeledDataSingle = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchStudioListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research_studio/single_survey_modeled_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q) => ({
                question_id: Number(q.question_id),
                kpi_id: Number(q.kpi_id),
                positive_responses: q.positive_responses ?? null,
                negative_responses: q.negative_responses ?? null,
              })),
              demographics: demographicsPayload(selectedDemographics.value),
              custom_demographics: customDemoIds,
              geographies: selectedGeographies.value,
              meta_audience_ids: selectedAudiences.value.map((a) => a.id),
            },
          },
        );
        taskIds.value.modeledDataSingle = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ResearchStudio",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.modeledDataSingle = undefined;
        loading.value.modeledDataSingle = false;
      }
    },
    async getModeledDataTrended() {
      const surveyId = selectedSurvey.value?.id;
      const questions = selectedQuestions.value;

      if (!surveyId || !questions?.length) return;
      await cancelTask("modeledDataTrended");
      errors.value.modeledDataTrended = undefined;
      loading.value.modeledDataTrended = true;
      try {
        const customDemoIds = selectedCustomDemos.value.map(
          (demo: schemas["ResearchStudioListCustomDemographicsResponse"]) =>
            demo.id,
        );
        const { data } = await client.POST(
          "/api/v1/research_studio/trended_survey_modeled_data",
          {
            body: {
              sync: false,
              survey_id: surveyId,
              questions: questions.map((q: any) => ({
                question_id: q.question_id,
                kpi_id: q.kpi_id,
                positive_responses: q.positive_responses ?? null,
                negative_responses: q.negative_responses ?? null,
              })),
              demographics: demographicsPayload(selectedDemographics.value),
              custom_demographics: customDemoIds,
              geographies: selectedGeographies.value,
              meta_audience_ids: selectedAudiences.value.map((a) => a.id),
            },
          },
        );
        taskIds.value.modeledDataTrended = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "ResearchStudio",
            payload: { id: data?.task_id },
          });
        }
      } catch (error) {
        console.error(error);
        taskIds.value.modeledDataTrended = undefined;
        loading.value.modeledDataTrended = false;
      }
    },
    async generateAiSummary(sync: boolean = false) {
      const surveyData = formatDataForAiSummary();
      const survey = selectedSurvey.value;

      errors.value.aiSummary = undefined;
      aiSummary.value = undefined;
      loading.value.aiSummary = true;

      if (!surveyData?.length || !survey) {
        errors.value.aiSummary = "No survey data available";
        loading.value.aiSummary = false;
        return;
      }

      await cancelTask("aiSummary");

      try {
        const { response, data } = await client.POST(
          "/api/v1/research_studio/survey_ai_summary",
          {
            body: {
              sync: sync,
              survey: toSurveyRequest(survey),
              survey_data: surveyData,
            },
          },
        );
        if (response.ok) {
          if (sync) {
            aiSummary.value = data?.summary;
            loading.value.aiSummary = false;
            return;
          }
          taskIds.value.aiSummary = data?.task_id;
          if ($ws) {
            $ws.subscribe({
              channel: "ResearchStudio",
              payload: { id: data?.task_id },
            });
          }
        } else {
          errors.value.aiSummary = "Error generating AI summary";
          loading.value.aiSummary = false;
        }
      } catch {
        errors.value.aiSummary = "Error generating AI summary";
        loading.value.aiSummary = false;
      }
    },
    // Forwarded to the websocket channel so it can resolve + download finished
    // exports without per-export-type branching.
    handleExportMessage: exportManager.handleMessage,
    clearExportErrors: exportManager.clearErrors,
    async createToplineExport() {
      const survey = selectedSurvey.value;
      const surveyData = formatDataForTopline(surveyDataSingle.value ?? []);

      if (!survey || isEmpty(survey) || !surveyData || !surveyData.length)
        return;

      await exportManager.startExport("topline", () =>
        client.POST("/api/v1/research_studio/export/topline", {
          body: {
            sync: false,
            survey: toSurveyRequest(survey),
            survey_data: surveyData,
          },
        }),
      );
    },
    async createCrosstabsExport(dataType: "survey_data" | "modeled_data") {
      const surveyData = formatDataForCrosstabs(dataType);

      if (
        !surveyData?.length ||
        !selectedSurvey.value?.id ||
        !selectedSurvey.value?.name
      )
        return;
      const { id: surveyId, name: surveyName } = selectedSurvey.value;

      await exportManager.startExport("crosstabs", () =>
        client.POST("/api/v1/research_studio/export/crosstabs", {
          body: {
            sync: false,
            survey_id: surveyId,
            survey_name: surveyName,
            data: surveyData as schemas["ResearchStudioSurveyDataExportRequest"]["data"],
            data_type: dataType,
          },
        }),
      );
    },
    async createRawDataExport() {
      if (!selectedSurvey.value?.id || !selectedSurvey.value?.name) return;
      const { id: surveyId, name: surveyName } = selectedSurvey.value;

      await exportManager.startExport("raw_data", () =>
        client.POST("/api/v1/research_studio/export/raw_data", {
          body: {
            sync: false,
            survey_id: surveyId,
            survey_name: surveyName,
          },
        }),
      );
    },
    async createPowerPointExport(dataType: "survey_data" | "modeled_data") {
      const surveyData = formatDataForCrosstabs(dataType);

      if (
        !surveyData?.length ||
        !selectedSurvey.value?.id ||
        !selectedSurvey.value?.name
      )
        return;
      const { id: surveyId, name: surveyName } = selectedSurvey.value;

      await exportManager.startExport("powerpoint", () =>
        client.POST("/api/v1/research_studio/export/powerpoint", {
          body: {
            sync: false,
            survey_id: surveyId,
            survey_name: surveyName,
            summary: aiSummary.value,
            data: surveyData as schemas["ResearchStudioSurveyDataExportRequest"]["data"],
            data_type: dataType,
          },
        }),
      );
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useResearchStudioStore, import.meta.hot),
  );
}
